import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { AiOutlineDownload, AiOutlineInfo } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import LineChart from "../../components/charts-Widgets/component/lineChart";
import EvdaFranceFilterWrapper from "../../components/filter-widget/evdaFranceFilterWrapper";
import TableComponent, { DataTable } from "../../components/table-widget/tableComponent";
import DataDisplaySwitcher from "../../components/templates/components/dataDisplaySwitcher";
import SmallFilterAccessor from "../../components/templates/components/smallFilterAccessor";
import PageTemplate from "../../components/templates/pageTemplate";
import { SelectedArea } from "../../models/SelectedArea";
import { SelectedGender } from "../../models/SelectedGender";
import { getFranceDisplayMode } from "../../redux/selectors/displayModeSelector";
import { getAreaFilterState, getEvdaFranceAgeFilterState, getEvdaFranceDateFilterState, getEvdaFranceGenderFilterState } from "../../redux/selectors/filterSelector";
import { DisplayModeAction } from "../../redux/states/displayModeState";
import { FilterAction } from "../../redux/states/filterState";
import Line from "../../service/Line";
import { filterData, generateDateArray, getMaxDate } from "../../service/dataFilter";
import getData from "../../service/dataLoader";
import Point from "../../service/point";
import { useWindowWidthAndHeight } from "../../shared/hooks/customHooks";
import { getRightAge } from "../../shared/utils/getRightAge";
import { GetCurveTitleForEVDAPage } from "../../shared/utils/getRightTitle";
import { convertNumberToString } from "../../shared/utils/numbersUtils";

const EvdaFranceWidget = () => {
  const dispatch = useDispatch();
  const [width] = useWindowWidthAndHeight();
  const { t, i18n } = useTranslation();
  const ageFilter = useSelector(getEvdaFranceAgeFilterState);
  const genderFilter = useSelector(getEvdaFranceGenderFilterState);
  const dateFilter = useSelector(getEvdaFranceDateFilterState);
  const areaFilter = useSelector(getAreaFilterState);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Filter components States
  const [curve_title_key, setCurveTitleKey] = useState<string | TemplateStringsArray | (string | TemplateStringsArray)[]>("evda_france_birth_curve_title");
  const [EvdaFranceWidgetData, setDeviFranceData] = useState<Line[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const displayMode = useSelector(getFranceDisplayMode);
  const [isDataLoaded, setDataState] = useState(false);
  const [deviFMaledataToDisplay, setDeviFMaleDataToDisplay] = useState<Point[]>([]);
  const [deviFWomendataToDisplay, setDeviFWomenDataToDisplay] = useState<Point[]>([]);
  const [tooltipId, setTooltipId] = useState<string>(displayMode.isChartMode ? "tableDisplayMode" : "chartDisplayMode");
  const [maxDate, setMaxDate] = useState<number>(0);

  useEffect(() => {
    dispatch(FilterAction.SetArea(SelectedArea.France));
    setDataState(true);
    if (!isDataLoaded) {
      getData(`en/outil-interactif/7344233/data/esperance_vie_france.csv`).then((lines) => {
        setDeviFranceData(lines.data);
      });
    }
  }, [isDataLoaded]);

  useEffect(() => {
    if (maxDate > 0) {
      if (dateFilter.selectedIntervalDate[0] === 0) {
        dispatch(FilterAction.setEvdaFranceDate([maxDate - 25, maxDate]));
      }
    }
  }, [maxDate]);
  function loadChart() {
    setLabels(generateDateArray(dateFilter.selectedIntervalDate));
  }

  useEffect(() => {
    const deviFMaleFiltredData = filterData(EvdaFranceWidgetData, SelectedGender.male, getRightAge(ageFilter.seletedAge, ageFilter.customSelectedAge), labels);
    const deviFWomenFiltredData = filterData(EvdaFranceWidgetData, SelectedGender.women, getRightAge(ageFilter.seletedAge, ageFilter.customSelectedAge), labels);

    setDeviFMaleDataToDisplay(deviFMaleFiltredData);
    setDeviFWomenDataToDisplay(deviFWomenFiltredData);
  }, [labels]);

  useEffect(() => {
    loadChart();
    if (EvdaFranceWidgetData.length > 0) {
      setMaxDate(getMaxDate(EvdaFranceWidgetData));
    }
  }, [EvdaFranceWidgetData]);

  useEffect(() => {
    loadChart();
    setCurveTitleKey(GetCurveTitleForEVDAPage(ageFilter.seletedAge, ageFilter.customSelectedAge, areaFilter.selectedArea, genderFilter.selectedGender));
  }, [ageFilter, genderFilter.selectedGender, areaFilter.selectedArea, dateFilter.selectedIntervalDate]);

  function provideValues(sourceData: Point[]) {
    const values: number[] = [];
    sourceData.forEach(function (point) {
      values.push(point.Y_axis);
    });
    return values;
  }

  function provideEcartValues(womenData: Point[], menData: Point[]) {
    const values: number[] = [];
    for (let i = 0; i < womenData.length; i++) {
      const ecart = womenData[i].Y_axis - menData[i].Y_axis;
      values.push(Math.round((ecart + Number.EPSILON) * 100) / 100);
    }
    return values;
  }

  const buildDataSets = () => {
    const dataSets: any[] = [];

    if (genderFilter.selectedGender.length > 1) {
      dataSets.push({
        label: t("women"),
        data: provideValues(deviFWomendataToDisplay),
        borderColor: "#0F893D",
        backgroundColor: "#0F893D",
        borderWidth: width > 1150 ? 2 : 1,
        pointStyle: "circle",
        tension: 0.2,
      });
      dataSets.push({
        label: t("men"),
        data: provideValues(deviFMaledataToDisplay),
        borderColor: "#0F417A",
        backgroundColor: "#0F417A",
        borderWidth: width > 1150 ? 2 : 1,
        pointStyle: "rect",
        tension: 0.2,
      });
    } else if (genderFilter.selectedGender.indexOf(SelectedGender.male) > -1) {
      dataSets.push({
        label: t("men"),
        data: provideValues(deviFMaledataToDisplay),
        borderColor: "#0F417A",
        backgroundColor: "#0F417A",
        borderWidth: width > 1150 ? 2 : 1,
        pointStyle: "rect",
        tension: 0.2,
      });
    } else if (genderFilter.selectedGender.indexOf(SelectedGender.women) > -1) {
      dataSets.push({
        label: t("women"),
        data: provideValues(deviFWomendataToDisplay),
        borderColor: "#0F893D",
        backgroundColor: "#0F893D",
        borderWidth: width > 1150 ? 2 : 1,
        pointStyle: "circle",
        tension: 0.2,
      });
    } else {
      //ecart
      dataSets.push({
        label: t("gap_short"),
        data: provideEcartValues(deviFWomendataToDisplay, deviFMaledataToDisplay),
        borderColor: "#EB617F",
        backgroundColor: "#EB617F",
        borderWidth: width > 1150 ? 2 : 1,
        pointStyle: "triangle",
        tension: 0.2,
      });
    }
    return dataSets;
  };

  useEffect(() => {
    setTooltipId(displayMode.isChartMode ? "chartDisplayMode" : "tableDisplayMode");
  }, [displayMode.isChartMode]);

  const provideTableTitles = () => {
    let titles: string[] = [];
    titles.push(t("year_observation"));
    if (genderFilter.selectedGender.indexOf(SelectedGender.male_women_diff) > -1) {
      titles.push(t("gap"));
    } else {
      if (genderFilter.selectedGender.indexOf(SelectedGender.women) > -1) {
        titles.push(t("women"));
      }
      if (genderFilter.selectedGender.indexOf(SelectedGender.male) > -1) {
        titles.push(t("men"));
      }
    }

    return titles;
  };

  const provideDataToTableDisplay = () => {
    let dataTable: DataTable[] = [];
    if (genderFilter.selectedGender.indexOf(SelectedGender.male_women_diff) > -1) {
      for (let i = 0; i < deviFWomendataToDisplay.length; i++) {
        const ecart = deviFWomendataToDisplay[i].Y_axis - deviFMaledataToDisplay[i].Y_axis;
        const element: DataTable = {
          key: "element" + i,
          columns: [{ data: labels[i], component: "th" }, { data: convertNumberToString(ecart, i18n, 1, 2) }],
        };
        dataTable.push(element);
      }
    } else {
      if (genderFilter.selectedGender.indexOf(SelectedGender.women) > -1 && genderFilter.selectedGender.indexOf(SelectedGender.male) === -1) {
        for (let i = 0; i < deviFWomendataToDisplay.length; i++) {
          const element: DataTable = {
            key: "element" + i,
            columns: [{ data: labels[i], component: "th" }, { data: convertNumberToString(deviFWomendataToDisplay[i].Y_axis, i18n, 1, 2) }],
          };
          dataTable.push(element);
        }
      } else if (genderFilter.selectedGender.indexOf(SelectedGender.male) > -1 && genderFilter.selectedGender.indexOf(SelectedGender.women) === -1) {
        for (let i = 0; i < deviFWomendataToDisplay.length; i++) {
          const element: DataTable = {
            key: "element" + i,
            columns: [{ data: labels[i], component: "th" }, { data: convertNumberToString(deviFMaledataToDisplay[i].Y_axis, i18n, 1, 2) }],
          };
          dataTable.push(element);
        }
      } else {
        for (let i = 0; i < deviFWomendataToDisplay.length; i++) {
          const element: DataTable = {
            key: "element" + i,
            columns: [{ data: labels[i], component: "th" }, { data: convertNumberToString(deviFWomendataToDisplay[i].Y_axis, i18n, 1, 2) }, { data: convertNumberToString(deviFMaledataToDisplay[i].Y_axis, i18n, 1, 2) }],
          };
          dataTable.push(element);
        }
      }
    }
    return dataTable;
  };

  const onSetDisplayModeEvent = () => {
    dispatch(DisplayModeAction.changeFranceDisplayMode());
  };

  const provideGraphContent = () => {
    const style1 = { color: "#ffffff" };
    const style2 = { color: "#0C3A5A" };

    return (
      <div className="display-data">
        <div className="display-data-header">
          <h3>
            {t("" + curve_title_key, {
              age: ageFilter.customSelectedAge,
              unit: ageFilter.customSelectedAge === 1 ? "an" : "ans",
            })}
          </h3>

          <div className="graph-button-container">
            {width < 1150 ? <SmallFilterAccessor></SmallFilterAccessor> : undefined}

            <DataDisplaySwitcher tooltipId="displayMode" isChartMode={displayMode.isChartMode} setModeHandler={onSetDisplayModeEvent}></DataDisplaySwitcher>

            <span aria-label="Informations supplémentaires sur les données d'espérance de vie en France" aria-details="infoTip" data-tip data-for="infoTip" className="info-round-button">
              <AiOutlineInfo style={style2}></AiOutlineInfo>
            </span>

            <a href={`en/outil-interactif/7344233/data/downloads/Esperance_Vie_FRANCE.xlsx`} aria-label="Téléchargement des données complètes d'espérance de vie en France au format .xlsx" data-tip data-for="downloadTip" className="download-round-button">
              <AiOutlineDownload style={style1}></AiOutlineDownload>
            </a>

            <ReactTooltip id="infoTip" place="bottom" effect="solid" multiline={true} html={true}>
              {t("tooltip-info-evda-departments")}
            </ReactTooltip>

            <ReactTooltip id="downloadTip" place="bottom" effect="solid" multiline={true} html={true}>
              {t("tooltip-download-evda-department")}
            </ReactTooltip>

            <ReactTooltip id="displayMode" place="bottom" effect="solid" multiline={true} html={true}>
              {tooltipId === "chartDisplayMode" ? t("tooltip-change-display-table-mode") : t("tooltip-change-display-chart-mode")}
            </ReactTooltip>
          </div>
        </div>
        {displayMode.isChartMode ? (
          <div className="line-container" ref={chartContainerRef}>
            {
              <LineChart
                ariaLabel={t("charts_aria-label")}
                labels={labels}
                dataSets={buildDataSets()}
                containerDimensions={{
                  height: chartContainerRef.current?.offsetHeight || 0,
                  width: chartContainerRef.current?.offsetWidth || 0,
                }}
              ></LineChart>
            }
          </div>
        ) : (
          <TableComponent
            caption={t("" + curve_title_key, {
              age: ageFilter.customSelectedAge,
              unit: ageFilter.customSelectedAge === 1 ? "an" : "ans",
            })}
            unit={t("year_unit_plural", { postProcess: "plural" })}
            data={provideDataToTableDisplay()}
            titles={provideTableTitles()}
          ></TableComponent>
        )}
      </div>
    );
  };

  const provideFilterWidget = () => {
    return <EvdaFranceFilterWrapper maxDate={maxDate} />;
  };

  return (
    <>
      <Helmet>
        <title>{t("evda-france-title")}</title>
      </Helmet>
      <PageTemplate pageName="France" dataSource={t("evda_france_data_source")} graphContent={provideGraphContent()} filterContent={provideFilterWidget()} />
    </>
  );
};

export default EvdaFranceWidget;
